// Layer 3 (Transformation): row projections for the three tab bodies (locked
// comp `planning.html` A): the monthly table, the annual table with its bullet
// column, the planning hierarchy and the Year-1 close by scenario. Figures are
// the existing views'; this file only formats, orders and places them.

import type { SessionState } from '@/versions/v1/schema/scenario';
import {
  formatPlanningMoney,
  formatPlanningValue,
  formatSignedPercent,
  formatSignedPlanningMoney,
  scenarioRecoveryBasis,
  type AnnualPlanView,
  type MonthlyPlanView,
  type PlanningScenarioName,
  type RollingForecastView,
} from './Scene.transform';
import { formatDst } from './PlanningBand.transform';
import { SCENARIO_ORDER, scenarioRateBasis } from './MonthClose.transform';

// ---- Monthly plan vs actual vs budget ------------------------------------

export type MonthlyTableRow = {
  id: string;
  label: string;
  current: boolean;
  budget: string;
  plan: string;
  actual?: string;
  /** The M4 actual's window and its comparator, on the line under it. */
  actualNote?: string;
  vsBudgetPct?: number;
  vsBudget?: string;
  /** Share of the diverging bar's half-width, 0–100. */
  vsBudgetBar?: number;
  znBudget: string;
  znActual?: string;
  znDeltaPpt?: number;
  znDelta?: string;
};

export type MonthlyTableRead = {
  /** Budget and Plan merge into one column when they agree in every row. */
  planEqualsBudget: boolean;
  capDecode: string;
  rows: MonthlyTableRow[];
  total: { label: string; budget: string; plan: string; actual: string; vsBudgetPct: number; vsBudget: string; znBudget: string; znActual: string; znDelta: string };
};

function pct(value: number): string {
  return `${value.toFixed(1)}%`;
}

function ppt(value: number): string {
  return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)} pt`;
}

export function monthlyTableRead(monthly: MonthlyPlanView): MonthlyTableRead {
  const planEqualsBudget = monthly.rows.every((row) => Math.abs(row.planSt - row.budgetSt) < 0.5)
    && Math.abs(monthly.yearToDatePlanSt - monthly.yearToDateBudgetSt) < 0.5;
  const halfWidth = Math.max(5, Math.ceil(Math.max(...monthly.rows.map((row) => Math.abs(row.vsBudgetPct ?? 0))) / 5) * 5);
  const check = monthly.ceilingCheck;
  const current = monthly.rows.findIndex((row) => row.actualWindowPlanSt !== undefined);
  const ytdDelta = monthly.yearToDateZnRecoveryPct - monthly.yearToDateZnBudgetPct;
  return {
    planEqualsBudget,
    capDecode: planEqualsBudget
      ? `dst · plan = budget: ${check.dailyTargetStpd.toLocaleString('en-US')} stpd under the ${formatDst(check.ceilingStpd)} stpd ${check.ceilingLabel}`
      : `dst · plan = budget capped at ${check.ceilingStph.toFixed(1)} stph × 24 = ${formatDst(check.ceilingStpd)} stpd ${check.ceilingLabel}`,
    rows: monthly.rows.map((row, index) => ({
      id: row.id,
      label: row.label,
      current: index === current,
      budget: formatDst(row.budgetSt),
      plan: formatDst(row.planSt),
      actual: row.actualSt === undefined ? undefined : formatDst(row.actualSt),
      actualNote: row.actualWindowPlanSt === undefined
        ? undefined
        : `${monthly.mtdWindowLabel} · vs ${formatDst(row.actualWindowPlanSt)} plan`,
      vsBudgetPct: row.vsBudgetPct,
      vsBudget: row.vsBudgetPct === undefined ? undefined : formatSignedPercent(row.vsBudgetPct),
      vsBudgetBar: row.vsBudgetPct === undefined ? undefined : Math.min(100, (Math.abs(row.vsBudgetPct) / halfWidth) * 100),
      znBudget: pct(row.budgetZnRecovery),
      znActual: row.actualZnRecovery === undefined ? undefined : pct(row.actualZnRecovery),
      znDeltaPpt: row.deltaZnPpt,
      znDelta: row.deltaZnPpt === undefined ? undefined : ppt(row.deltaZnPpt),
    })),
    total: {
      label: 'Year 1 to date',
      budget: formatDst(monthly.yearToDateBudgetSt),
      plan: formatDst(monthly.yearToDatePlanSt),
      actual: formatDst(monthly.yearToDateActualSt),
      vsBudgetPct: monthly.yearToDateVsBudgetPct,
      vsBudget: formatSignedPercent(monthly.yearToDateVsBudgetPct),
      znBudget: pct(monthly.yearToDateZnBudgetPct),
      znActual: pct(monthly.yearToDateZnRecoveryPct),
      znDelta: ppt(ytdDelta),
    },
  };
}

// ---- Annual plan structure --------------------------------------------------

export type AnnualTableRow = {
  id: string;
  label: string;
  unit: string;
  provenance: string;
  design: string;
  budget: string;
  /** Zn recovery only: the annual budget as a share of design. */
  budgetNote?: string;
  stretch: string;
  toDate: string;
  belowBudget: boolean;
  /** Bullet geometry on a design-scaled track (rate rows only; annual totals
   *  against a part-year actual would mislead, so they carry none). */
  bullet?: { toDatePct: number; budgetPct: number; stretchPct: number };
};

const UNIT_LABEL: Record<string, string> = { stpd: 'stpd', '%': '%', 'dst/year': 'dst/yr' };

function unitLabel(id: string, unit: string): string {
  if (id === 'pb-grade') return '% Pb';
  if (id === 'zn-grade') return '% Zn';
  return UNIT_LABEL[unit] ?? unit;
}

function stripUnit(label: string): string {
  return label.replace(/\s*\([^)]*\)\s*$/, '');
}

export function annualTableRead(annual: AnnualPlanView): AnnualTableRow[] {
  const znShare = (annual.annualZnRecoveryBudgetPct / annual.annualZnRecoveryDesignPct) * 100;
  return annual.rows.map((row) => {
    const scale = (value: number) => Math.max(0, Math.min(100, (value / row.design) * 100));
    const rate = row.unit !== 'dst/year';
    return {
      id: row.id,
      label: stripUnit(row.label),
      unit: unitLabel(row.id, row.unit),
      provenance: row.provenance,
      design: formatPlanningValue(row, row.design).replace(/ (stpd|dst)$/, ''),
      budget: formatPlanningValue(row, row.annualBudget).replace(/ (stpd|dst)$/, ''),
      budgetNote: row.id === 'zn-recovery' ? `${znShare.toFixed(1)}% of design` : undefined,
      stretch: formatPlanningValue(row, row.stretch).replace(/ (stpd|dst)$/, ''),
      toDate: formatPlanningValue(row, row.yearToDateActual).replace(/ (stpd|dst)$/, ''),
      belowBudget: rate && row.yearToDateActual < row.annualBudget,
      bullet: rate
        ? { toDatePct: scale(row.yearToDateActual), budgetPct: scale(row.annualBudget), stretchPct: scale(row.stretch) }
        : undefined,
    };
  });
}

// ---- Planning hierarchy ------------------------------------------------------

export type HierarchyRow = {
  id: string;
  label: string;
  depth: number;
  budget: string;
  ceiling: string;
  /** D27: `design` (Year 1) or the Capacity binding limit (`grinding ceiling`). */
  capLabel: string;
  basis: string;
  budgetPctOfCeiling: number;
  note?: string;
};

export function hierarchyRead(annual: AnnualPlanView): { planEqualsBudget: boolean; rows: HierarchyRow[]; capDecode: string } {
  const recovery = annual.month4RecoveryTargets
    .map((target) => `${target.parameter.slice(0, 2)} ${target.budgetPct.toFixed(1)}`)
    .join(' · ');
  return {
    planEqualsBudget: annual.hierarchy.every((row) => Math.abs(row.planSt - row.budgetSt) < 0.5),
    capDecode: `Bar = budget against the period's ${[...new Set(annual.hierarchy.map((row) => row.capLabel))].join(' or ')} (tick)`,
    rows: annual.hierarchy.map((row, depth) => ({
      id: row.id,
      label: row.label,
      depth,
      budget: `${formatDst(row.budgetSt)} dst`,
      ceiling: formatDst(row.ceilingSt),
      capLabel: row.capLabel,
      basis: row.ceilingBasis,
      budgetPctOfCeiling: Math.min(100, (row.planSt / row.ceilingSt) * 100),
      note: row.id === 'year'
        ? `${annual.scheduledDays} scheduled days`
        : row.id === 'month'
          ? `recovery budget ${recovery}%`
          : undefined,
    })),
  };
}

// ---- Year 1 close by scenario ------------------------------------------------

export type ScenarioTableRow = {
  name: PlanningScenarioName;
  rate: string;
  basis: string;
  /** The recovery assumption behind the rest of Year 1 (tooltip, not glass). */
  recoveryBasis: string;
  tonnes: string;
  barPct: number;
  vsBudget: string;
  znRec: string;
  nsr: string;
  nsrVsBudget: string;
};

export type ScenarioTableRead = {
  formula: string;
  rows: ScenarioTableRow[];
  budget: { tonnes: string; nsr: string };
  budgetTickPct: number;
  stretchTickPct: number;
  domainDecode: string;
};

export function scenarioTableRead(
  monthly: MonthlyPlanView,
  annual: AnnualPlanView,
  rolling: RollingForecastView,
  state: SessionState,
): ScenarioTableRead {
  const rates: Record<PlanningScenarioName, number> = {
    Optimistic: rolling.optimisticStph,
    Central: rolling.sevenDayStph,
    Conservative: rolling.conservativeStph,
  };
  const top = Math.max(...rolling.scenarios.map((scenario) => scenario.fullYearTonnes));
  const lo = Math.floor((annual.annualBudgetSt * 0.95) / 10_000) * 10_000;
  const hi = Math.ceil((top * 1.05) / 10_000) * 10_000;
  const place = (value: number) => Math.max(0, Math.min(100, ((value - lo) / (hi - lo)) * 100));
  return {
    formula: `${formatDst(monthly.yearToDateActualSt)} dst to date + ${Math.round(monthly.forecastRemainingHours).toLocaleString('en-US')} h × rate`,
    rows: SCENARIO_ORDER.map((name) => {
      const scenario = rolling.scenarios.find((row) => row.name === name)!;
      return {
        name,
        rate: `${rates[name].toFixed(1)} stph`,
        basis: scenarioRateBasis(rolling)[name].basis,
        recoveryBasis: scenarioRecoveryBasis(name, state).provenance,
        tonnes: formatDst(scenario.fullYearTonnes),
        barPct: place(scenario.fullYearTonnes),
        vsBudget: formatSignedPercent(scenario.vsBudgetPct),
        znRec: pct(scenario.restOfYearZnRecoveryPct),
        nsr: formatPlanningMoney(scenario.fullYearNsr),
        nsrVsBudget: formatSignedPlanningMoney(scenario.nsrVsBudget),
      };
    }),
    budget: { tonnes: formatDst(annual.annualBudgetSt), nsr: formatPlanningMoney(rolling.budgetNsr) },
    budgetTickPct: place(annual.annualBudgetSt),
    stretchTickPct: place(annual.annualStretchSt),
    domainDecode: `Bar from ${Math.round(lo / 1000)}k dst · solid tick = budget · dashed tick = ${formatDst(annual.annualStretchSt)} stretch`,
  };
}
